import { AlertCircle } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import type { PayinRuntimeEntity } from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { OperationDrawer } from "@/modules/dashboard/payments-activity/components/OperationDrawer";
import { useSelectedOperation } from "@/modules/dashboard/payments-activity/hooks/useSelectedOperation";
import { adaptPayinRow } from "@/modules/dashboard/payments-activity/utils/activityAdapters";
import { ACTIVITY_OPERATION_PARAM } from "@/modules/dashboard/payments-activity/utils/activityLinks";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";

import { WalletMovementsList } from "../components/WalletMovementsList";
import { WalletPendingCollections } from "../components/WalletPendingCollections";
import { WalletStatementDetailDialog } from "../components/WalletStatementDetailDialog";
import { WalletStatementFilters } from "../components/WalletStatementFilters";
import { WalletStatementHero } from "../components/WalletStatementHero";
import { isCryptoCurrency } from "../constants/walletMethods.constants";
import {
  CURRENCY_TO_COUNTRY,
  CURRENCY_TO_DISPERSION_CODE,
  CURRENCY_TO_FLAG_CODE,
  STATEMENT_MOVEMENT_PARAM,
} from "../constants/wallets.constants";
import { useOperationLabels } from "../hooks/useOperationLabels";
import { useStatementMovement } from "../hooks/useStatementMovement";
import { useWalletEntryLabels } from "../hooks/useWalletEntryLabels";
import { useWalletPendingCollections } from "../hooks/useWalletPendingCollections";
import { STATEMENT_PAGE_SIZE, useWalletStatement } from "../hooks/useWalletStatement";
import type { RuntimeWalletTransaction } from "../types/payments-wallets.types";
import type { RecentTransaction } from "../types/wallets-api.types";
import { mapRuntimeTransaction } from "../utils/mapRuntimeTransaction";

export const WalletMovementsPage = () => {
  const { t } = useTranslations("wallets");
  const { t: tGlobal } = useTranslations("global");
  const { walletId = "" } = useParams<{ walletId: string }>();
  const operationLabels = useOperationLabels();
  const { describe, statusOf } = useWalletEntryLabels();

  const {
    wallet,
    transactions,
    total,
    page,
    setPage,
    filters,
    setFilters,
    isLoading,
    isLoadingWallet,
    hasAccess,
    error,
    refresh,
  } = useWalletStatement(walletId);

  const { collections, refresh: refreshCollections } = useWalletPendingCollections(wallet);

  /**
   * The open collection lives in the URL, like the activity screen's drawer: that makes it linkable (support
   * can be pointed straight at a payer's pending QR) and lets the back button close it. Opening pushes a
   * history entry; closing replaces, so clicking through rows does not pile them up.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const openCollectionId = searchParams.get(ACTIVITY_OPERATION_PARAM);
  const collectionRows = useMemo(() => collections.map(adaptPayinRow), [collections]);
  // Same resolver the activity screen uses, so a link to a collection that has since settled (or that never
  // was in this wallet's pending list) still opens instead of leaving the URL claiming an open drawer.
  const { row: openCollectionRow, notFound: collectionNotFound } = useSelectedOperation(
    "payins",
    openCollectionId,
    collectionRows,
  );

  const openCollectionDrawer = useCallback(
    (payin: PayinRuntimeEntity) => {
      const next = new URLSearchParams(searchParams);
      next.set(ACTIVITY_OPERATION_PARAM, payin.id);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const closeCollectionDrawer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(ACTIVITY_OPERATION_PARAM);
    setSearchParams(next, { replace: true });
    // A collection that just settled leaves this band and shows up in the statement instead.
    refreshCollections();
    void refresh();
  }, [searchParams, setSearchParams, refreshCollections, refresh]);

  const breadcrumb = useMemo(() => {
    const items: { label: string; to?: string }[] = [
      { label: tGlobal("sidebar_section_billeteras") },
      { label: tGlobal("sidebar_menu_wallets_home"), to: "/app/wallets" },
    ];
    if (wallet) {
      const country = CURRENCY_TO_COUNTRY[wallet.currency];
      items.push({ label: country ? `${wallet.currency} · ${country}` : wallet.currency });
    }
    return items;
  }, [tGlobal, wallet]);
  useAppShellBreadcrumb(breadcrumb);

  const mappedTransactions = useMemo(
    () => transactions.map((tx) => mapRuntimeTransaction(tx, operationLabels, describe, statusOf)),
    [transactions, operationLabels, describe, statusOf],
  );
  const transactionById = useMemo(() => {
    const map = new Map<string, RuntimeWalletTransaction>();
    for (const tx of transactions) map.set(tx.id, tx);
    return map;
  }, [transactions]);

  // The open movement lives in the URL too, so a link opens it directly — even off the loaded page.
  const openMovementId = searchParams.get(STATEMENT_MOVEMENT_PARAM);
  const { transaction: openMovement, notFound: movementNotFound } = useStatementMovement(
    walletId,
    openMovementId,
    transactionById,
  );

  const handleSelect = useCallback(
    (tx: RecentTransaction) => {
      const next = new URLSearchParams(searchParams);
      next.set(STATEMENT_MOVEMENT_PARAM, tx.id);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const closeMovement = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(STATEMENT_MOVEMENT_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const flagCode =
    wallet && !isCryptoCurrency(wallet.currency)
      ? (CURRENCY_TO_FLAG_CODE[wallet.currency] ?? CURRENCY_TO_DISPERSION_CODE[wallet.currency] ?? null)
      : null;
  const hasFiltersActive =
    filters.operation_type !== "all" ||
    filters.direction !== "all" ||
    filters.from !== "" ||
    filters.to !== "";

  // Access latches on a 401/403 from the runtime.
  if (!hasAccess) {
    return <NoAccessView />;
  }

  if (isLoadingWallet && !wallet) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <span className="text-sm text-muted-foreground">{t("loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!wallet) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-4 p-2">
        <ColocaAlert variant="destructive" icon={AlertCircle} text={error ?? t("error_load")} />
        <div className="flex justify-center">
          <ColocaButton variant="secondary" size="sm" onClick={() => void refresh()}>
            {t("retry")}
          </ColocaButton>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-5 p-2">
      <WalletStatementHero wallet={wallet} onStatusChanged={refresh} />

      {/* A link whose collection cannot be read at all — deleted, or another organization's — is said out
          loud rather than dropped, the same way the activity screen handles a dead deep link. */}
      {collectionNotFound && (
        <ColocaAlert
          variant="warning"
          icon={AlertCircle}
          text={t("pending_collections_not_found")}
          actions={
            <ColocaButton variant="secondary" size="sm" onClick={closeCollectionDrawer}>
              {t("pending_collections_dismiss")}
            </ColocaButton>
          }
        />
      )}

      {movementNotFound && (
        <ColocaAlert
          variant="warning"
          icon={AlertCircle}
          text={t("statement_movement_not_found")}
          actions={
            <ColocaButton variant="secondary" size="sm" onClick={closeMovement}>
              {t("pending_collections_dismiss")}
            </ColocaButton>
          }
        />
      )}

      {/* Above the filters on purpose: these are not statement rows and the filters do not apply to them. */}
      <WalletPendingCollections collections={collections} onSelect={openCollectionDrawer} />

      <WalletStatementFilters filters={filters} onChange={setFilters} />

      {error ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={error}
          actions={
            <ColocaButton variant="secondary" size="sm" onClick={() => void refresh()}>
              {t("retry")}
            </ColocaButton>
          }
        />
      ) : (
        <>
          <WalletMovementsList
            transactions={mappedTransactions}
            isLoading={isLoading}
            hasFiltersActive={hasFiltersActive}
            flagCode={flagCode}
            onSelect={handleSelect}
          />
          {total > STATEMENT_PAGE_SIZE && (
            <ColocaPagination
              page={page}
              perPage={STATEMENT_PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <WalletStatementDetailDialog
        transaction={openMovement}
        wallet={wallet}
        operationLabels={operationLabels}
        onClose={closeMovement}
      />

      {/* Same drawer the activity screen opens, so a pending collection reads identically in both places —
          and it is what re-serves the QR while the payin is unpaid and unexpired. */}
      {openCollectionRow && <OperationDrawer row={openCollectionRow} open onClose={closeCollectionDrawer} />}
    </div>
  );
};
